import difference from 'lodash/difference';
import isEqual from 'lodash/isEqual';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useQueries } from '@hero/hooks';
import { useGetCategories } from '@hero/services/product-service/hooks';

import { CustomFilter } from '../CustomFilter';

function CategoryFilter({ label, categoryId }) {
  const { t } = useTranslation();

  const { queries, addQuery } = useQueries();
  const { data: categoriesData } = useGetCategories(null, {
    staleTime: Infinity,
    cacheTime: Infinity
  });

  const [selectedOptions, setSelectedOptions] = useState([]);

  const categoryIds = useMemo(() => {
    return (
      (typeof queries.categoryIds === 'string' ? [queries.categoryIds] : queries.categoryIds) || []
    );
  }, [queries.categoryIds]);

  const categoryFilterOptions = useMemo(() => {
    const filteredCategoriesById = categoriesData?.docs.find(c => categoryId == c.id);

    return (
      filteredCategoriesById?.subCategories?.map(category => ({
        value: category.id,
        label: category.name
      })) || []
    );
  }, [categoriesData]);

  useEffect(() => {
    const selectedOptionsData = categoryFilterOptions?.filter(
      c => categoryIds?.indexOf?.(c.value) > -1
    );

    if (!isEqual(selectedOptionsData, selectedOptions)) {
      setSelectedOptions(selectedOptionsData);
    }
  }, [categoryFilterOptions, categoryIds]);

  const previousSelectedOptions = categoryFilterOptions?.filter(c =>
    categoryIds?.includes?.(c.value)
  );

  return (
    <CustomFilter
      size="md"
      creatable={false}
      placeholder={label}
      selectedOption={selectedOptions}
      options={categoryFilterOptions}
      searchPlaceholder={`${t('searchProducts')}...`}
      hasMenuSearch
      onChange={setSelectedOptions}
      styles={{
        placeholder: (provided, state) => {
          if (state.hasValue) {
            return {
              ...provided,
              color: '#0D1E46',
              fontWeight: 700
            };
          }
          return { ...provided, color: '#475F8B' };
        }
      }}
      onApply={() => {
        const pOptionKeys = previousSelectedOptions.map(p => p.value);
        const sOptionKeys = selectedOptions.map(p => p.value);

        const removedOptions = difference(pOptionKeys, sOptionKeys) || [];
        const addedOptions = difference(sOptionKeys, pOptionKeys) || [];

        if (addedOptions.length || removedOptions.length) {
          addQuery(
            'categoryIds',
            Array.from(
              new Set([
                ...categoryIds.filter(cId => !removedOptions.includes(cId)),
                ...addedOptions
              ])
            )
          );
        }
      }}
      controlWidth={160}
    />
  );
}

export { CategoryFilter };
